import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import {
  Alert, Box, Button, CircularProgress, TextField, Typography,
} from '@mui/material';
import { useFamilyChild } from './FamilyShell';
import { parentApi } from './parentApi';
import AuthImage from '../../components/AuthImage';

export default function FamilyHomeworkSubmit() {
  const { sessionId } = useParams();
  const { child } = useFamilyChild();
  const navigate = useNavigate();
  const q = useQuery(
    ['familyHomework', child.studentId],
    async () => (await parentApi.homework(child.studentId)).data?.data || [],
    { enabled: !!child.studentId }
  );
  const item = (q.data || []).find((r) => r.sessionId === sessionId);
  const [text, setText] = useState(item?.submission?.body || '');
  const [files, setFiles] = useState([]);
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);

  const send = async () => {
    setErr('');
    if (!text.trim() && files.length === 0 && !item?.submission) {
      setErr('Add a photo or type what was done.');
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('body', text);
      files.forEach((f) => fd.append('files', f));
      await parentApi.submitHomework(child.studentId, sessionId, fd);
      navigate('/family/homework');
    } catch (e) {
      setErr(e.response?.data?.message || 'Could not send.');
    } finally {
      setSaving(false);
    }
  };

  if (q.isLoading) return <CircularProgress />;
  if (!item) return <Typography color="text.secondary">This homework is not available.</Typography>;

  const locked = item.status === 'reviewed';

  return (
    <Box>
      <Typography variant="h6">{item.instruction || (item.images?.length ? 'Screenshot homework' : 'Homework')}</Typography>
      {(item.images || []).length > 0 ? (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', my: 1 }}>
          {item.images.map((img) => (
            <AuthImage
              key={img.id}
              src={img.url}
              alt={img.name || 'Homework screenshot'}
              style={{ width: 180, height: 180, objectFit: 'cover', borderRadius: 8 }}
            />
          ))}
        </Box>
      ) : null}
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        {item.teacherName ? `${item.teacherName} sent this.` : ''}
        {item.dueDate ? ` Due ${item.dueDate}.` : ''}
      </Typography>
      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
      {item.teacherFileUrl ? (
        <Button href={item.teacherFileUrl} target="_blank" rel="noreferrer" sx={{ mb: 1 }}>Teacher file</Button>
      ) : null}
      {item.submission?.body ? (
        <Typography variant="body2" sx={{ mb: 1 }}>{item.submission.body}</Typography>
      ) : null}
      {(item.submission?.files || []).map((f) => (
        <Button key={f.url} href={f.url} target="_blank" rel="noreferrer">{f.name || 'Photo'}</Button>
      ))}
      {locked ? (
        <Alert severity="success" sx={{ mt: 1 }}>Reviewed. You can still read what was sent.</Alert>
      ) : (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 2 }}>
            <Button variant="outlined" component="label" sx={{ minHeight: 88 }}>
              Take a photo
              <input
                hidden
                type="file"
                accept="image/*,.pdf"
                capture="environment"
                multiple
                onChange={(e) => setFiles(Array.from(e.target.files || []))}
              />
            </Button>
            <Button variant="outlined" sx={{ minHeight: 88 }} onClick={() => document.getElementById('hw-type')?.focus()}>
              Type my answer
            </Button>
          </Box>
          {files.map((f) => (
            <Typography key={f.name} variant="body2">{f.name}</Typography>
          ))}
          <TextField
            id="hw-type"
            fullWidth
            multiline
            minRows={3}
            label="What I did"
            value={text}
            onChange={(e) => setText(e.target.value)}
            sx={{ my: 2 }}
          />
          <Button variant="contained" onClick={send} disabled={saving}>
            {saving ? <CircularProgress size={20} color="inherit" /> : `Send to ${item.teacherName || 'teacher'}`}
          </Button>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            They'll see it before the next class.
          </Typography>
        </>
      )}
    </Box>
  );
}
